import { RADIUS } from "@elyxeer/ui";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

/**
 * One purchase option on the membership screen — the placeholder's plan card grown
 * a price row and a button to the product page. Copy comes from the catalogues; the price and
 * the plan name come from Shopify, so the card never invents commercial facts.
 */
export interface PlanCardProps {
  label: string;
  title: string;
  description: string;
  /** Already formatted for the locale (`formatCurrency`). */
  price: string;
  /** e.g. "per delivery" on the subscription card. */
  priceSuffix?: string;
  /** The Shopify selling-plan name, shown verbatim under the price. */
  planName?: string;
  /** Small print under the button (the one-time "no app membership" note). */
  note?: string;
  recommended?: boolean;
  busy?: boolean;
  disabled?: boolean;
  ctaLabel: string;
  onSelect: () => void;
  testId: string;
  ctaTestId: string;
}

export function PlanCard({
  label,
  title,
  description,
  price,
  priceSuffix,
  planName,
  note,
  recommended = false,
  busy = false,
  disabled = false,
  ctaLabel,
  onSelect,
  testId,
  ctaTestId,
}: PlanCardProps) {
  return (
    <Paper
      variant="outlined"
      data-testid={testId}
      sx={{
        padding: 3,
        display: "flex",
        flexDirection: "column",
        gap: 1,
        // The recommended plan is marked with the brand colour rather than a badge.
        borderColor: recommended ? "primary.main" : "divider",
        borderRadius: `${RADIUS.lg}px`,
      }}
    >
      <Typography variant="OV1" sx={{ color: "text.muted" }}>
        {label}
      </Typography>
      <Typography variant="H3" sx={{ color: "text.primary" }}>
        {title}
      </Typography>
      <Typography variant="T2" sx={{ color: "text.secondary" }}>
        {description}
      </Typography>

      <Typography
        component="p"
        sx={{ display: "flex", alignItems: "baseline", gap: 1, marginTop: 1 }}
      >
        <Typography
          variant="H3"
          component="span"
          sx={{ color: "text.primary" }}
        >
          {price}
        </Typography>
        {priceSuffix ? (
          <Typography
            variant="T3"
            component="span"
            sx={{ color: "text.secondary" }}
          >
            {priceSuffix}
          </Typography>
        ) : null}
      </Typography>
      {planName ? (
        <Typography variant="L3" sx={{ color: "text.muted" }}>
          {planName}
        </Typography>
      ) : null}

      <Button
        variant={recommended ? "contained" : "outlined"}
        size="large"
        disabled={disabled || busy}
        onClick={onSelect}
        data-testid={ctaTestId}
        endIcon={
          busy ? <CircularProgress size={16} color="inherit" /> : undefined
        }
        sx={{ alignSelf: "flex-start", marginTop: 1 }}
      >
        {ctaLabel}
      </Button>

      {note ? (
        <Typography variant="T3" sx={{ color: "text.muted" }}>
          {note}
        </Typography>
      ) : null}
    </Paper>
  );
}
